/**
 * TC Whiskey — Cowlendar service booking → Authorize.net Accept Hosted intercept.
 *
 * Eligibility (no hardcoded product/variant/service allowlist):
 * - Legitimate Cowlendar bookings carry __cow_internal_id + __cow_integrity.
 * - Ordinary Shopify products (alcohol/swag) never include those properties → AccelPay.
 * - Worker independently validates Cowlendar service ↔ Shopify product/variant + price.
 *
 * Runtime notes:
 * - Cowlendar booking UI runs in a cross-origin iframe (widget.cowlendar.com).
 * - Parent receives completed cart/booking data via window.postMessage, then
 *   BookEasy posts to /cart/add.js.
 * - cowlendarBookRequest is optional — iframe /book body is not parent-visible.
 */
(function () {
  'use strict';

  var WORKER_HOSTED_TOKEN_URL =
    'https://tcwhiskey-service-payments.steep-heart-8393.workers.dev/payments/authorize/hosted-token';

  /** Origins allowed to supply Cowlendar booking/cart messages. */
  var TRUSTED_MESSAGE_ORIGINS = {
    'https://widget.cowlendar.com': true,
    'https://app.cowlendar.com': true
  };

  var LOG_PREFIX = '[TCW service-payments]';
  var DEBUG =
    /(?:\?|&)tcw_sp_debug=1(?:&|$)/.test(String(window.location.search || '')) ||
    (function () {
      try {
        return window.localStorage.getItem('tcw_sp_debug') === '1';
      } catch (e) {
        return false;
      }
    })();
  var lastBookByServiceId = {};
  var lastBookByVariantId = {};
  var lastRawMessages = [];
  var requestFieldHitsAcrossMessages = [];
  var paymentInFlight = false;
  var MAX_RAW_MESSAGES = 12;

  /** Optional /book request field names tracked when debug logging is enabled. */
  var BOOK_REQUEST_FIELD_NAMES = [
    'form_data',
    'quantity_details',
    'purchase_plan',
    'type',
    'locale',
    'start_date',
    'end_date',
    'timezone',
    'create_customer',
    'create_order',
    'use_prepayment',
    'variant_id',
    'is_ampm_format',
    'is_manual',
    'is_pos',
    'parent_internal_id',
    'teammate_id'
  ];

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    console.log.apply(console, args);
  }

  function warn() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    console.warn.apply(console, args);
  }

  function errorLog() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    console.error.apply(console, args);
  }

  /** Redact email-like strings from support logs. */
  function redactEmail(value) {
    var s = String(value || '');
    return s.replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '[redacted-email]');
  }

  function showServicePaymentError(message) {
    try {
      window.alert(
        'Sorry — we could not start checkout for this booking.\n\n' +
          message +
          '\n\nPlease try again or contact tours@tcwhiskey.com.'
      );
    } catch (e) {
      /* ignore */
    }
  }

  /** Deliberate modal dismiss — must not surface as a checkout failure. */
  function createUserCancelledError() {
    var err = new Error('USER_CANCELLED');
    err.code = 'USER_CANCELLED';
    err.userCancelled = true;
    return err;
  }

  function isUserCancelledError(err) {
    return Boolean(err && (err.userCancelled === true || err.code === 'USER_CANCELLED'));
  }

  function toAbsoluteUrl(url) {
    try {
      return new URL(url, window.location.origin).href;
    } catch (e) {
      return String(url || '');
    }
  }

  function isCowlendarBookUrl(url) {
    return /app\.cowlendar\.com\/extapi\/calendar\/[^/]+\/book/i.test(toAbsoluteUrl(url));
  }

  function extractServiceIdFromBookUrl(url) {
    var match = toAbsoluteUrl(url).match(
      /app\.cowlendar\.com\/extapi\/calendar\/([^/]+)\/book/i
    );
    return match ? match[1] : null;
  }

  function isCartAddUrl(url) {
    var absolute = toAbsoluteUrl(url);
    return /\/cart\/add\.js(?:\?|$)/i.test(absolute) || /\/cart\/add(?:\?|$)/i.test(absolute);
  }

  function safeJsonParse(text) {
    try {
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  }

  function deepClone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  /**
   * Serialize for diagnostics. Prefer JSON so nested objects are fully visible
   * in console copy/paste (DevTools object previews truncate).
   */
  function serializeForDiagnostics(value) {
    var seen = [];
    try {
      return JSON.stringify(
        value,
        function (_key, current) {
          if (typeof current === 'bigint') return String(current);
          if (typeof current === 'function') return '[Function]';
          if (current && typeof current === 'object') {
            if (seen.indexOf(current) !== -1) return '[Circular]';
            seen.push(current);
          }
          return current;
        },
        2
      );
    } catch (e) {
      try {
        return String(value);
      } catch (e2) {
        return '[unserializable]';
      }
    }
  }

  function safeDeepClone(value) {
    try {
      return deepClone(value);
    } catch (e) {
      return null;
    }
  }

  function walkObject(root, visitor, maxNodes) {
    if (!root || typeof root !== 'object') return;
    var queue = [{ path: '', node: root }];
    var seen = 0;
    var limit = typeof maxNodes === 'number' ? maxNodes : 200;
    while (queue.length && seen < limit) {
      var item = queue.shift();
      seen += 1;
      visitor(item.node, item.path);
      if (!item.node || typeof item.node !== 'object') continue;
      objectKeys(item.node).forEach(function (key) {
        var child = item.node[key];
        if (child && typeof child === 'object') {
          queue.push({
            path: item.path ? item.path + '.' + key : key,
            node: child
          });
        }
      });
    }
  }

  function scanRequestFieldHits(root) {
    var hits = {};
    walkObject(root, function (node) {
      if (!node || typeof node !== 'object' || Array.isArray(node)) return;
      BOOK_REQUEST_FIELD_NAMES.forEach(function (field) {
        if (Object.prototype.hasOwnProperty.call(node, field) && hits[field] == null) {
          hits[field] = safeDeepClone(node[field]);
        }
      });
    });
    return hits;
  }

  function classifyTrustedMessage(parsed) {
    if (looksLikeBookRequest(parsed) && parsed.form_data) return 'book-request-with-form_data';
    if (looksLikeBookRequest(parsed)) return 'book-request-like';
    var cartNode = findCartDataNode(parsed);
    if (cartNode && lineItemsHaveCowSignals(cartNode.line_items)) {
      return 'cow-cart-data';
    }
    var hits = scanRequestFieldHits(parsed);
    if (objectKeys(hits).length) return 'partial-request-fields';
    return 'other-trusted';
  }

  function summarizeLineItems(lineItems) {
    if (!Array.isArray(lineItems)) return [];
    return lineItems.map(function (item) {
      var props = (item && item.properties) || {};
      var slots = (item && item.slots) || [];
      var firstSlot = slots[0] || null;
      return {
        variant_id: item && (item.variant_id || item.id),
        quantity: item && item.quantity,
        propertyKeys: objectKeys(props),
        hasSlots: Array.isArray(slots) && slots.length > 0,
        slotCount: Array.isArray(slots) ? slots.length : 0,
        slot_start: firstSlot && firstSlot.slot_start,
        slot_end: firstSlot && firstSlot.slot_end,
        timezone: firstSlot && firstSlot.timezone,
        Date: props.Date || null,
        __cow_internal_id: props.__cow_internal_id || null
      };
    });
  }

  function readBodyAsText(body) {
    if (body == null) return Promise.resolve('');
    if (typeof body === 'string') return Promise.resolve(body);
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return Promise.resolve(body.toString());
    }
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      var params = new URLSearchParams();
      body.forEach(function (value, key) {
        params.append(key, value);
      });
      return Promise.resolve(params.toString());
    }
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
      return body.text();
    }
    if (body && typeof body.text === 'function') {
      return body.text();
    }
    return Promise.resolve(String(body));
  }

  function normalizeProperties(props) {
    if (!props || typeof props !== 'object') return {};
    var out = {};
    Object.keys(props).forEach(function (key) {
      out[key] = props[key] == null ? '' : String(props[key]);
    });
    return out;
  }

  function parseCartAddLines(bodyText) {
    var lines = [];
    var text = (bodyText || '').trim();
    if (!text) return lines;

    var json = safeJsonParse(text);
    if (json) {
      if (Array.isArray(json.items)) {
        json.items.forEach(function (item) {
          lines.push({
            variantId: String(item.id || item.variant_id || ''),
            quantity: Number(item.quantity || 1),
            properties: normalizeProperties(item.properties || {})
          });
        });
        return lines;
      }
      if (json.id || json.variant_id) {
        lines.push({
          variantId: String(json.id || json.variant_id || ''),
          quantity: Number(json.quantity || 1),
          properties: normalizeProperties(json.properties || {})
        });
        return lines;
      }
    }

    try {
      var params = new URLSearchParams(text);
      var id = params.get('id') || params.get('items[0][id]');
      if (id) {
        var properties = {};
        params.forEach(function (value, key) {
          var m =
            key.match(/^properties\[(.+)\]$/) ||
            key.match(/^items\[0\]\[properties\]\[(.+)\]$/);
          if (m) properties[m[1]] = value;
        });
        lines.push({
          variantId: String(id),
          quantity: Number(params.get('quantity') || params.get('items[0][quantity]') || 1),
          properties: normalizeProperties(properties)
        });
      }
    } catch (e) {
      /* ignore */
    }
    return lines;
  }

  /** Structural Cowlendar signal — not a product-id allowlist. */
  function isCowlendarServiceLine(line) {
    if (!line || !line.variantId) return false;
    var props = line.properties || {};
    return Boolean(props.__cow_internal_id && props.__cow_integrity);
  }

  function findServiceLine(lines) {
    for (var i = 0; i < lines.length; i += 1) {
      if (isCowlendarServiceLine(lines[i])) return lines[i];
    }
    return null;
  }

  function lineItemsHaveCowSignals(lineItems) {
    if (!Array.isArray(lineItems) || !lineItems.length) return false;
    for (var i = 0; i < lineItems.length; i += 1) {
      var props = (lineItems[i] && lineItems[i].properties) || {};
      if (props.__cow_internal_id && props.__cow_integrity) {
        return true;
      }
    }
    return false;
  }

  function objectKeys(value) {
    if (!value || typeof value !== 'object') return [];
    try {
      return Object.keys(value);
    } catch (e) {
      return [];
    }
  }

  function looksLikeBookRequest(obj) {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
    return Boolean(
      obj.form_data ||
        obj.quantity_details ||
        obj.purchase_plan ||
        (obj.start_date && obj.end_date && (obj.variant_id || obj.type))
    );
  }

  function extractBookRequestFromMessagePayload(payload) {
    if (!payload || typeof payload !== 'object') return null;
    var candidates = [
      payload.book_request,
      payload.bookRequest,
      payload.request,
      payload.booking_request,
      payload.bookingRequest,
      payload.cowlendarBookRequest,
      payload.form_payload,
      payload
    ];
    for (var i = 0; i < candidates.length; i += 1) {
      if (looksLikeBookRequest(candidates[i]) && candidates[i].form_data) {
        return deepClone(candidates[i]);
      }
    }
    for (var j = 0; j < candidates.length; j += 1) {
      if (looksLikeBookRequest(candidates[j])) {
        return deepClone(candidates[j]);
      }
    }
    return null;
  }

  function findCartDataNode(root) {
    if (!root) return null;
    var queue = [root];
    var seen = 0;
    while (queue.length && seen < 40) {
      seen += 1;
      var node = queue.shift();
      if (!node || typeof node !== 'object') continue;
      if (Array.isArray(node.line_items) && node.line_items.length) {
        return node;
      }
      objectKeys(node).forEach(function (key) {
        var child = node[key];
        if (child && typeof child === 'object') queue.push(child);
      });
    }
    return null;
  }

  function storeBookCapture(serviceId, bookRequest, bookResponse, meta) {
    if (!bookResponse) return;
    // Keep the exact trusted payload (including { action, data: { line_items } }).
    var response = deepClone(bookResponse);
    var lineItems = findLineItemsArray(response);
    if (!lineItems || !lineItems.length) return;

    var action =
      typeof response.action === 'string' && response.action
        ? response.action
        : 'add_to_cart';
    if (action !== 'add_to_cart') {
      warn('Ignoring non add_to_cart capture action', action);
      return;
    }

    var resolvedServiceId = serviceId || null;

    var capture = {
      serviceId: resolvedServiceId,
      bookRequest: bookRequest ? deepClone(bookRequest) : null,
      bookResponse: response,
      source: (meta && meta.source) || 'unknown',
      rawMessage: meta && meta.rawMessage ? deepClone(meta.rawMessage) : null,
      capturedAt: Date.now()
    };

    if (resolvedServiceId) lastBookByServiceId[resolvedServiceId] = capture;
    for (var i = 0; i < lineItems.length; i += 1) {
      var variantId = String(lineItems[i].variant_id || lineItems[i].id || '');
      if (variantId) lastBookByVariantId[variantId] = capture;
    }

    log('Stored Cowlendar booking capture', {
      source: capture.source,
      serviceId: resolvedServiceId,
      hasBookRequest: Boolean(capture.bookRequest),
      hasBookResponse: true,
      variantIds: lineItems.map(function (item) {
        return String(item.variant_id || item.id || '');
      })
    });
  }

  function resolveCaptureForLine(line, fallbackServiceId) {
    if (line && line.variantId && lastBookByVariantId[line.variantId]) {
      return lastBookByVariantId[line.variantId];
    }
    if (fallbackServiceId && lastBookByServiceId[fallbackServiceId]) {
      return lastBookByServiceId[fallbackServiceId];
    }
    return null;
  }

  function getActiveCurrency() {
    try {
      if (window.Shopify && Shopify.currency && Shopify.currency.active) {
        return String(Shopify.currency.active).toUpperCase();
      }
    } catch (e) {
      /* ignore */
    }
    return 'USD';
  }

  function findLineItemsArray(bookResponse) {
    if (!bookResponse || typeof bookResponse !== 'object') return null;
    if (Array.isArray(bookResponse.line_items)) return bookResponse.line_items;
    if (bookResponse.data && Array.isArray(bookResponse.data.line_items)) {
      return bookResponse.data.line_items;
    }
    var cartNode = findCartDataNode(bookResponse);
    return cartNode && Array.isArray(cartNode.line_items) ? cartNode.line_items : null;
  }

  /**
   * Fail closed unless response-equivalent capture has required booking fields.
   */
  function validateRequiredBookingCapture(capture, line) {
    if (!capture || !capture.bookResponse) {
      return {
        ok: false,
        error:
          'Missing captured Cowlendar booking response in the Shopify parent page. ' +
          'Wait for the trusted postMessage capture and try again.'
      };
    }

    var lineItems = findLineItemsArray(capture.bookResponse);
    if (!lineItems || !lineItems.length) {
      return { ok: false, error: 'Captured booking response is missing line_items.' };
    }

    var item = null;
    for (var i = 0; i < lineItems.length; i += 1) {
      var variantId = String(lineItems[i].variant_id || lineItems[i].id || '');
      if (variantId === String(line.variantId)) {
        item = lineItems[i];
        break;
      }
    }
    if (!item) item = lineItems[0];

    var props = (item && item.properties) || {};
    var slots = (item && item.slots) || [];
    var slot = slots[0];
    var quantity = Number(item && item.quantity);

    if (!props.__cow_internal_id || !props.__cow_integrity) {
      return { ok: false, error: 'Captured booking is missing __cow_internal_id / __cow_integrity.' };
    }
    if (
      line.properties &&
      line.properties.__cow_internal_id &&
      line.properties.__cow_internal_id !== props.__cow_internal_id
    ) {
      return {
        ok: false,
        error: 'Captured booking __cow_internal_id does not match the cart line.'
      };
    }
    if (!slot || !slot.slot_start || !slot.slot_end || !slot.timezone) {
      return {
        ok: false,
        error: 'Captured booking is missing required slot_start / slot_end / timezone.'
      };
    }
    if (!Number.isFinite(quantity) || quantity < 1) {
      return { ok: false, error: 'Captured booking is missing a valid quantity.' };
    }

    return {
      ok: true,
      lineItem: item,
      slot: slot,
      quantity: quantity,
      displayDate: props.Date || '',
      cowInternalId: props.__cow_internal_id,
      cowIntegrity: props.__cow_integrity
    };
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  }

  var LIABILITY_WAIVER_PATH = '/pages/liability-waiver';

  function getLiabilityWaiverUrl() {
    try {
      return new URL(LIABILITY_WAIVER_PATH, window.location.origin).href;
    } catch (e) {
      return LIABILITY_WAIVER_PATH;
    }
  }

  function waiverTextLooksLikeNoise(text) {
    var t = String(text || '').toLowerCase();
    if (!t.trim()) return true;
    return (
      t.indexOf('entice customers to sign up') !== -1 ||
      t.indexOf('mailing list') !== -1 ||
      t.indexOf('sign up and save') !== -1 ||
      t.indexOf('exclusive offers') !== -1
    );
  }

  function isExcludedPageChrome(el) {
    if (!el || !el.closest) return true;
    return Boolean(
      el.closest(
        'header, footer, nav, .section-footer, .announcement-bar, .site-header, .modal, .popup, [id*="pop-up"], [class*="newsletter"], [class*="signup"], form'
      )
    );
  }

  /**
   * Extract Shopify PAGE body content only (never a global/newsletter .rte).
   * Strategy: resolve page main → page template .rte inside that main.
   */
  function extractLiabilityWaiverContent(html) {
    if (!html || typeof DOMParser === 'undefined') {
      return { ok: false, html: '', selector: null };
    }
    var doc = new DOMParser().parseFromString(html, 'text/html');

    // Deterministic page root — do not search the whole document for .rte.
    var pageRoot =
      doc.querySelector('#MainContent') ||
      doc.querySelector('main#content') ||
      doc.querySelector('main[role="main"]') ||
      doc.querySelector('main');
    if (!pageRoot) {
      return { ok: false, html: '', selector: null };
    }

    var node = null;
    var matchedSelector = null;

    // 1) Standard page template: container that holds .page-header + .rte (page.content).
    var pageHeader = pageRoot.querySelector('.page-header');
    if (pageHeader) {
      var headerContainer =
        (pageHeader.closest && pageHeader.closest('.container')) ||
        pageHeader.parentElement;
      if (headerContainer) {
        var headerRte = null;
        var kids = headerContainer.children || [];
        for (var k = 0; k < kids.length; k += 1) {
          var kid = kids[k];
          if (
            kid &&
            kid.classList &&
            kid.classList.contains('rte') &&
            !isExcludedPageChrome(kid)
          ) {
            headerRte = kid;
            break;
          }
        }
        if (!headerRte) {
          // Sibling after page-header within the same parent.
          var sib = pageHeader.nextElementSibling;
          while (sib) {
            if (sib.classList && sib.classList.contains('rte') && !isExcludedPageChrome(sib)) {
              headerRte = sib;
              break;
            }
            sib = sib.nextElementSibling;
          }
        }
        if (
          headerRte &&
          headerRte.textContent &&
          headerRte.textContent.replace(/\s+/g, ' ').trim() &&
          !waiverTextLooksLikeNoise(headerRte.textContent)
        ) {
          node = headerRte;
          matchedSelector = 'main .page-header container > .rte';
        }
      }
    }

    // 2) Scoped page-section / reading container .rte under page root only.
    if (!node) {
      var scopedSelectors = [
        '.page-section-spacing .rte',
        '.container--reading-width .rte',
        '.container.cf > .rte',
        '.container > .rte'
      ];
      for (var s = 0; s < scopedSelectors.length; s += 1) {
        var scoped = pageRoot.querySelector(scopedSelectors[s]);
        if (
          scoped &&
          !isExcludedPageChrome(scoped) &&
          scoped.textContent &&
          scoped.textContent.replace(/\s+/g, ' ').trim() &&
          !waiverTextLooksLikeNoise(scoped.textContent)
        ) {
          node = scoped;
          matchedSelector = 'main ' + scopedSelectors[s];
          break;
        }
      }
    }

    // 3) First non-chrome .rte inside page root (still never document-global).
    if (!node) {
      var rtes = pageRoot.querySelectorAll('.rte');
      for (var r = 0; r < rtes.length; r += 1) {
        var candidate = rtes[r];
        if (isExcludedPageChrome(candidate)) continue;
        var text = candidate.textContent
          ? candidate.textContent.replace(/\s+/g, ' ').trim()
          : '';
        if (!text || waiverTextLooksLikeNoise(text)) continue;
        node = candidate;
        matchedSelector = 'main .rte (first non-chrome)';
        break;
      }
    }

    // 4) Last resort: page meta description when theme template does not render page.content.
    if (!node) {
      var meta =
        doc.querySelector('meta[name="description"]') ||
        doc.querySelector('meta[property="og:description"]');
      var metaText = meta && meta.getAttribute('content')
        ? String(meta.getAttribute('content')).trim()
        : '';
      if (metaText && !waiverTextLooksLikeNoise(metaText)) {
        return {
          ok: true,
          html: '<p>' + metaText.replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</p>',
          selector: 'meta[name=description]'
        };
      }
      return { ok: false, html: '', selector: null };
    }

    var clone = node.cloneNode(true);
    var junk = clone.querySelectorAll(
      'script, style, link, iframe, noscript, nav, header, footer, form, .announcement-bar, .site-header, .section-footer'
    );
    for (var j = 0; j < junk.length; j += 1) {
      try {
        junk[j].remove();
      } catch (e) {
        /* ignore */
      }
    }

    var htmlOut = (clone.innerHTML || '').trim();
    var plain = (clone.textContent || '').replace(/\s+/g, ' ').trim();
    if (!htmlOut || !plain || waiverTextLooksLikeNoise(plain)) {
      return { ok: false, html: '', selector: matchedSelector };
    }
    return { ok: true, html: htmlOut, selector: matchedSelector };
  }

  function ensureCustomerModalStyles() {
    if (document.getElementById('tcw-service-pay-styles')) return;
    var style = document.createElement('style');
    style.id = 'tcw-service-pay-styles';
    // Layout/positioning only — colors/typography lean on theme CSS variables + .btn / input rules.
    style.textContent =
      '#tcw-service-pay-overlay{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:1.25rem;background:rgba(0,0,0,.55);font-family:var(--base-font-family,sans-serif);font-size:var(--base-text-size,16px);font-weight:var(--base-font-weight,400);font-style:var(--base-font-style,normal);line-height:var(--base-line-height,1.6);color:rgb(var(--body-text-color,0,0,0));-webkit-font-smoothing:antialiased;}' +
      '#tcw-service-pay-modal{width:min(100%,26.5rem);max-height:min(92vh,40rem);overflow:auto;background:rgb(var(--body-bg-color,255,255,255));color:rgb(var(--body-text-color,0,0,0));border:1px solid rgba(var(--body-text-color,0,0,0),.12);border-radius:var(--btn-border-radius,0);box-shadow:0 16px 40px rgba(0,0,0,.22);padding:1.35rem 1.25rem 1.2rem;}' +
      '#tcw-service-pay-title{margin:0 0 .65rem;}' +
      '#tcw-service-pay-summary{margin:0 0 1rem;padding:.65rem .75rem;background:rgba(var(--body-text-color,0,0,0),.04);border:1px solid rgba(var(--body-text-color,0,0,0),.08);font-size:var(--smaller-text-size-2,calc(var(--base-text-size,16px) - 2px));line-height:1.4;font-weight:700;}' +
      '#tcw-service-pay-modal .tcw-field-label{display:block;margin:0 0 .35rem;font-family:var(--base-font-family,sans-serif);font-size:var(--smaller-text-size-1,calc(var(--base-text-size,16px) * .8));font-weight:600;letter-spacing:normal;text-transform:none;color:rgb(var(--body-text-color,0,0,0));}' +
      '#tcw-service-pay-modal input[type="text"],#tcw-service-pay-modal input[type="email"],#tcw-service-pay-modal input[type="tel"]{width:100%;margin:0 0 .85rem;}' +
      '#tcw-service-pay-waiver-row.terms-agreement{display:flex;align-items:flex-start;gap:.65rem;margin:0 0 .35rem;}' +
      '#tcw-service-pay-waiver{margin:.2rem 0 0;flex:0 0 auto;}' +
      '#tcw-service-pay-waiver-copy{margin:0;flex:1;font-size:inherit;line-height:1.45;}' +
      '#tcw-service-pay-waiver-copy label{display:inline;margin:0;vertical-align:baseline;cursor:pointer;}' +
      '#tcw-service-pay-waiver-link{display:inline;padding:0;margin:0;border:0;background:none;color:rgb(var(--color-scheme-default-link-color,var(--body-text-color,0,0,0)));font:inherit;text-decoration:underline;cursor:pointer;}' +
      '#tcw-service-pay-waiver-link:focus{outline:2px solid rgb(var(--btn-bg-color,161,132,48));outline-offset:2px;}' +
      '#tcw-service-pay-required-key{margin:0 0 .9rem;font-size:var(--smaller-text-size-3,calc(var(--base-text-size,16px) - 3px));line-height:1.3;opacity:.7;}' +
      '#tcw-service-pay-error{display:none;margin:0 0 .85rem;color:#9b1c1c;font-size:var(--smaller-text-size-2,calc(var(--base-text-size,16px) - 2px));}' +
      '#tcw-service-pay-actions{display:flex;flex-wrap:wrap;gap:.65rem;margin-top:.15rem;}' +
      '#tcw-service-pay-actions .btn{flex:1 1 8rem;text-align:center;}' +
      '#tcw-service-pay-submit[disabled]{opacity:.45;cursor:not-allowed;}' +
      '#tcw-service-pay-waiver-overlay{position:fixed;inset:0;z-index:2147483001;display:flex;align-items:center;justify-content:center;padding:1rem;background:rgba(0,0,0,.55);font-family:var(--base-font-family,sans-serif);color:rgb(var(--body-text-color,0,0,0));}' +
      '#tcw-service-pay-waiver-panel{width:min(100%,40rem);height:min(88vh,40rem);display:flex;flex-direction:column;background:rgb(var(--body-bg-color,255,255,255));border:1px solid rgba(var(--body-text-color,0,0,0),.12);border-radius:var(--btn-border-radius,0);box-shadow:0 16px 40px rgba(0,0,0,.22);overflow:hidden;}' +
      '#tcw-service-pay-waiver-header{display:flex;align-items:center;justify-content:space-between;gap:.75rem;padding:.85rem 1rem;border-bottom:1px solid rgba(var(--body-text-color,0,0,0),.12);flex:0 0 auto;}' +
      '#tcw-service-pay-waiver-header .tcw-waiver-title{margin:0;}' +
      '#tcw-service-pay-waiver-close{display:inline-flex;align-items:center;justify-content:center;width:2.25rem;height:2.25rem;padding:0;border:0;background:transparent;color:inherit;font-size:1.5rem;line-height:1;cursor:pointer;}' +
      '#tcw-service-pay-waiver-close:focus{outline:2px solid rgb(var(--btn-bg-color,161,132,48));outline-offset:2px;}' +
      '#tcw-service-pay-waiver-body{flex:1 1 auto;min-height:0;overflow:auto;padding:1rem 1.1rem;-webkit-overflow-scrolling:touch;}' +
      '#tcw-service-pay-waiver-body.rte>:first-child{margin-top:0;}' +
      '#tcw-service-pay-waiver-footer{flex:0 0 auto;padding:.85rem 1rem 1rem;border-top:1px solid rgba(var(--body-text-color,0,0,0),.12);display:flex;justify-content:center;}' +
      '#tcw-service-pay-waiver-agree{min-width:min(100%,12rem);}' +
      '#tcw-service-pay-waiver-status{margin:0;font-size:var(--smaller-text-size-2,calc(var(--base-text-size,16px) - 2px));line-height:1.5;}' +
      '#tcw-service-pay-waiver-status a{color:rgb(var(--color-scheme-default-link-color,var(--body-text-color,0,0,0)));}';
    document.head.appendChild(style);
  }

  function canContinueToPayment(name, email, phone, waiverChecked) {
    return Boolean(
      String(name || '').trim() &&
        isValidEmail(email) &&
        String(phone || '').trim() &&
        waiverChecked === true
    );
  }

  function collectCustomerInfo(bookingSummary) {
    ensureCustomerModalStyles();
    return new Promise(function (resolve, reject) {
      var existing = document.getElementById('tcw-service-pay-overlay');
      if (existing) existing.remove();
      var existingWaiver = document.getElementById('tcw-service-pay-waiver-overlay');
      if (existingWaiver) existingWaiver.remove();

      var previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      var overlay = document.createElement('div');
      overlay.id = 'tcw-service-pay-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-labelledby', 'tcw-service-pay-title');

      overlay.innerHTML =
        '<div id="tcw-service-pay-modal">' +
        '<h2 id="tcw-service-pay-title" class="h4 heading-font">Reservation Details</h2>' +
        '<div id="tcw-service-pay-summary"></div>' +
        '<label class="tcw-field-label" for="tcw-service-pay-name">Full name *</label>' +
        '<input id="tcw-service-pay-name" name="name" type="text" autocomplete="name" required />' +
        '<label class="tcw-field-label" for="tcw-service-pay-email">Email *</label>' +
        '<input id="tcw-service-pay-email" name="email" type="email" autocomplete="email" required />' +
        '<label class="tcw-field-label" for="tcw-service-pay-phone">Phone number *</label>' +
        '<input id="tcw-service-pay-phone" name="phone" type="tel" autocomplete="tel" required />' +
        '<div id="tcw-service-pay-waiver-row" class="terms-agreement rte">' +
        '<input id="tcw-service-pay-waiver" name="liability_waiver" type="checkbox" value="1" />' +
        '<div id="tcw-service-pay-waiver-copy">' +
        '<label for="tcw-service-pay-waiver">I have read and agree to the </label>' +
        '<button type="button" id="tcw-service-pay-waiver-link">Liability Waiver</button>' +
        '<span aria-hidden="true"> *</span>' +
        '</div>' +
        '</div>' +
        '<p id="tcw-service-pay-required-key">(*) required</p>' +
        '<div id="tcw-service-pay-error"></div>' +
        '<div id="tcw-service-pay-actions">' +
        '<button type="button" id="tcw-service-pay-cancel" class="btn btn--secondary">Cancel</button>' +
        '<button type="button" id="tcw-service-pay-submit" class="btn btn--primary" disabled>Continue to payment</button>' +
        '</div>' +
        '</div>';

      document.body.appendChild(overlay);

      var summary = document.getElementById('tcw-service-pay-summary');
      var nameInput = document.getElementById('tcw-service-pay-name');
      var emailInput = document.getElementById('tcw-service-pay-email');
      var phoneInput = document.getElementById('tcw-service-pay-phone');
      var waiverInput = document.getElementById('tcw-service-pay-waiver');
      var waiverLink = document.getElementById('tcw-service-pay-waiver-link');
      var errorEl = document.getElementById('tcw-service-pay-error');
      var submitBtn = document.getElementById('tcw-service-pay-submit');
      var cancelBtn = document.getElementById('tcw-service-pay-cancel');
      var waiverOverlay = null;

      summary.textContent =
        (bookingSummary.displayDate
          ? bookingSummary.displayDate
          : bookingSummary.slotStart + ' – ' + bookingSummary.slotEnd) +
        ' · ' +
        bookingSummary.quantity +
        (bookingSummary.quantity === 1 ? ' guest' : ' guests');

      function cleanup() {
        closeWaiverOverlay();
        document.body.style.overflow = previousOverflow;
        document.removeEventListener('keydown', onKeyDown, true);
        try {
          overlay.remove();
        } catch (e) {
          /* ignore */
        }
      }

      function showError(message) {
        errorEl.style.display = 'block';
        errorEl.textContent = message;
      }

      function clearError() {
        errorEl.style.display = 'none';
        errorEl.textContent = '';
      }

      function syncSubmitEnabled() {
        var enabled = canContinueToPayment(
          nameInput.value,
          emailInput.value,
          phoneInput.value,
          waiverInput.checked
        );
        submitBtn.disabled = !enabled;
      }

      function closeWaiverOverlay(options) {
        var opts = options || {};
        if (!waiverOverlay) return;
        try {
          waiverOverlay.remove();
        } catch (e) {
          /* ignore */
        }
        waiverOverlay = null;
        try {
          if (opts.focusWaiver) {
            waiverInput.focus();
          } else {
            nameInput.focus();
          }
        } catch (e2) {
          /* ignore */
        }
      }

      function agreeToWaiver() {
        waiverInput.checked = true;
        try {
          waiverInput.dispatchEvent(new Event('change', { bubbles: true }));
          waiverInput.dispatchEvent(new Event('input', { bubbles: true }));
        } catch (e) {
          /* ignore */
        }
        syncSubmitEnabled();
        clearError();
        closeWaiverOverlay({ focusWaiver: true });
      }

      function renderWaiverFailure(bodyEl, waiverUrl) {
        bodyEl.className = '';
        bodyEl.innerHTML =
          '<p id="tcw-service-pay-waiver-status">' +
          'Sorry — we could not load the Liability Waiver here. ' +
          '<a href="' +
          waiverUrl +
          '" target="_blank" rel="noopener noreferrer">Open Liability Waiver</a>' +
          ' in a new tab, then return here to continue.' +
          '</p>';
      }

      function openWaiverOverlay() {
        if (waiverOverlay) return;
        var waiverUrl = getLiabilityWaiverUrl();
        waiverOverlay = document.createElement('div');
        waiverOverlay.id = 'tcw-service-pay-waiver-overlay';
        waiverOverlay.setAttribute('role', 'dialog');
        waiverOverlay.setAttribute('aria-modal', 'true');
        waiverOverlay.setAttribute('aria-label', 'Liability Waiver');
        waiverOverlay.innerHTML =
          '<div id="tcw-service-pay-waiver-panel">' +
          '<div id="tcw-service-pay-waiver-header">' +
          '<h3 class="h5 heading-font tcw-waiver-title">Liability Waiver</h3>' +
          '<button type="button" id="tcw-service-pay-waiver-close" aria-label="Close liability waiver">×</button>' +
          '</div>' +
          '<div id="tcw-service-pay-waiver-body" class="rte" tabindex="0">' +
          '<p id="tcw-service-pay-waiver-status">Loading Liability Waiver…</p>' +
          '</div>' +
          '<div id="tcw-service-pay-waiver-footer">' +
          '<button type="button" id="tcw-service-pay-waiver-agree" class="btn btn--primary">I Agree</button>' +
          '</div>' +
          '</div>';
        document.body.appendChild(waiverOverlay);

        var closeBtn = document.getElementById('tcw-service-pay-waiver-close');
        var agreeBtn = document.getElementById('tcw-service-pay-waiver-agree');
        var bodyEl = document.getElementById('tcw-service-pay-waiver-body');
        closeBtn.addEventListener('click', function (event) {
          event.preventDefault();
          closeWaiverOverlay();
        });
        agreeBtn.addEventListener('click', function (event) {
          event.preventDefault();
          agreeToWaiver();
        });
        waiverOverlay.addEventListener('click', function (event) {
          if (event.target === waiverOverlay) closeWaiverOverlay();
        });
        try {
          closeBtn.focus();
        } catch (e) {
          /* ignore */
        }

        // Same-origin fetch + DOM extract (Shopify blocks framing via CSP/XFO).
        fetch(waiverUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' }
        })
          .then(function (response) {
            if (!response.ok) {
              throw new Error('Waiver page returned ' + response.status);
            }
            return response.text();
          })
          .then(function (html) {
            if (!waiverOverlay || !bodyEl.isConnected) return;
            var extracted = extractLiabilityWaiverContent(html);
            if (!extracted.ok) {
              renderWaiverFailure(bodyEl, waiverUrl);
              return;
            }
            bodyEl.className = 'rte';
            bodyEl.innerHTML = extracted.html;
            log('Liability waiver content loaded', {
              selector: extracted.selector,
              url: waiverUrl
            });
          })
          .catch(function (err) {
            if (!waiverOverlay || !bodyEl.isConnected) return;
            errorLog('Liability waiver fetch failed', err);
            renderWaiverFailure(bodyEl, waiverUrl);
          });
      }

      function onKeyDown(event) {
        if (event.key !== 'Escape') return;
        if (waiverOverlay) {
          event.preventDefault();
          event.stopPropagation();
          closeWaiverOverlay();
        }
      }

      cancelBtn.addEventListener('click', function () {
        cleanup();
        reject(createUserCancelledError());
      });

      waiverLink.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        openWaiverOverlay();
      });

      nameInput.addEventListener('input', syncSubmitEnabled);
      emailInput.addEventListener('input', syncSubmitEnabled);
      phoneInput.addEventListener('input', syncSubmitEnabled);
      waiverInput.addEventListener('change', syncSubmitEnabled);
      document.addEventListener('keydown', onKeyDown, true);

      submitBtn.addEventListener('click', function () {
        var name = String(nameInput.value || '').trim();
        var email = String(emailInput.value || '').trim().toLowerCase();
        var phone = String(phoneInput.value || '').trim();
        if (!name) {
          showError('Please enter your full name.');
          nameInput.focus();
          syncSubmitEnabled();
          return;
        }
        if (!isValidEmail(email)) {
          showError('Please enter a valid email address.');
          emailInput.focus();
          syncSubmitEnabled();
          return;
        }
        if (!phone) {
          showError('Please enter your phone number.');
          phoneInput.focus();
          syncSubmitEnabled();
          return;
        }
        if (!waiverInput.checked) {
          showError('Please agree to the Liability Waiver to continue.');
          waiverInput.focus();
          syncSubmitEnabled();
          return;
        }
        clearError();
        var parts = name.split(/\s+/);
        var customer = {
          name: name,
          email: email,
          phone: phone,
          firstName: parts[0],
          lastName: parts.length > 1 ? parts.slice(1).join(' ') : undefined,
          liabilityWaiver: {
            accepted: true,
            acceptedAt: new Date().toISOString(),
            url: LIABILITY_WAIVER_PATH
          }
        };
        cleanup();
        resolve(customer);
      });

      syncSubmitEnabled();
      setTimeout(function () {
        try {
          nameInput.focus();
        } catch (e) {
          /* ignore */
        }
      }, 0);
    });
  }

  function submitAcceptHostedForm(paymentFormUrl, token) {
    var form = document.createElement('form');
    form.method = 'POST';
    form.action = paymentFormUrl;
    form.acceptCharset = 'UTF-8';
    form.style.display = 'none';

    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'token';
    input.value = token;
    form.appendChild(input);

    document.body.appendChild(form);
    log('Submitting Accept Hosted form to', paymentFormUrl);
    form.submit();
  }

  function routeToHostedPayment(line) {
    if (paymentInFlight) {
      return Promise.reject(new Error('A service payment redirect is already in progress'));
    }

    if (!isCowlendarServiceLine(line)) {
      return Promise.reject(new Error('Cart line is not a Cowlendar service booking'));
    }

    var capture = resolveCaptureForLine(line, null);
    var validated = validateRequiredBookingCapture(capture, line);
    if (!validated.ok) {
      return Promise.reject(new Error(validated.error));
    }

    // Prefer the exact trusted parent MessageEvent payload for Worker/KV.
    var bookResponse = deepClone(
      (capture.rawMessage && typeof capture.rawMessage === 'object'
        ? capture.rawMessage
        : capture.bookResponse)
    );
    var bookRequest =
      capture.bookRequest && typeof capture.bookRequest === 'object'
        ? deepClone(capture.bookRequest)
        : null;

    return collectCustomerInfo({
      displayDate: validated.displayDate,
      slotStart: validated.slot.slot_start,
      slotEnd: validated.slot.slot_end,
      quantity: validated.quantity
    }).then(function (customerResult) {
      var customer = {
        name: customerResult.name,
        email: customerResult.email,
        phone: customerResult.phone,
        firstName: customerResult.firstName,
        lastName: customerResult.lastName
      };

      var payload = {
        currency: getActiveCurrency(),
        customer: customer,
        liabilityWaiver: customerResult.liabilityWaiver,
        cowlendarBookResponse: bookResponse
      };
      if (capture.serviceId && capture.serviceId !== 'pending' && capture.serviceId !== 'unknown') {
        payload.cowlendarServiceId = capture.serviceId;
      }
      if (bookRequest) {
        payload.cowlendarBookRequest = bookRequest;
      }

      paymentInFlight = true;
      log('Routing service booking to Worker hosted-token', {
        variantId: line.variantId,
        serviceId: payload.cowlendarServiceId || null,
        quantity: line.quantity,
        cowInternalId: line.properties.__cow_internal_id,
        captureSource: capture.source,
        hasBookRequest: Boolean(bookRequest),
        customerEmail: redactEmail(customer.email),
        hasPhone: Boolean(customer.phone),
        liabilityWaiverAccepted: Boolean(
          payload.liabilityWaiver && payload.liabilityWaiver.accepted
        )
      });

      return fetch(WORKER_HOSTED_TOKEN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          return response.text().then(function (text) {
            var data = safeJsonParse(text) || {};
            if (!response.ok || !data.ok || !data.token || !data.paymentFormUrl) {
              var message =
                (data && data.error) ||
                'Hosted payment token request failed (' + response.status + ')';
              throw new Error(message);
            }
            return data;
          });
        })
        .then(function (data) {
          submitAcceptHostedForm(data.paymentFormUrl, data.token);
          return new Promise(function () {});
        })
        .catch(function (err) {
          paymentInFlight = false;
          throw err;
        });
    });
  }

  function maybeInterceptCartAdd(bodyText) {
    var lines = parseCartAddLines(bodyText);
    var serviceLine = findServiceLine(lines);
    if (!serviceLine) return null;

    log('Intercepting service /cart/add.js', {
      variantId: serviceLine.variantId,
      quantity: serviceLine.quantity,
      properties: serviceLine.properties
    });

    return routeToHostedPayment(serviceLine).catch(function (err) {
      // User closed the reservation modal — stay on the product page, no alert/side effects.
      if (isUserCancelledError(err)) {
        log('Reservation details modal dismissed by customer');
        return { ok: false, cancelled: true };
      }
      errorLog('Service payment intercept failed', err);
      showServicePaymentError(err && err.message ? err.message : String(err));
      return Promise.reject(err);
    });
  }

  function captureBookFromNetwork(url, requestBodyText, responseText) {
    if (!isCowlendarBookUrl(url)) return;
    var serviceId = extractServiceIdFromBookUrl(url);
    var bookResponse = safeJsonParse(responseText);
    var bookRequest = safeJsonParse(requestBodyText);
    if (!serviceId || !bookResponse) return;
    storeBookCapture(serviceId, bookRequest, bookResponse, { source: 'parent-network-/book' });
  }

  /* -------------------- postMessage observation (iframe → parent) -------------------- */

  function ingestParentMessage(origin, data) {
    var parsed = data;
    if (typeof data === 'string') {
      parsed = safeJsonParse(data) || data;
    }

    var clonedData = safeDeepClone(parsed);
    var dataJson = serializeForDiagnostics(parsed);
    var fieldHits = scanRequestFieldHits(parsed);
    var classification = classifyTrustedMessage(parsed);
    var cartNode = findCartDataNode(parsed);
    var lineSummary = cartNode ? summarizeLineItems(cartNode.line_items) : [];

    var diagnostic = {
      origin: origin,
      receivedAt: new Date().toISOString(),
      dataType: typeof parsed,
      dataKeys: objectKeys(parsed),
      classification: classification,
      requestFieldHits: fieldHits,
      requestFieldHitKeys: objectKeys(fieldHits),
      lineItemSummary: lineSummary,
      // Full nested payload for copy/paste (not key summaries only).
      data: clonedData != null ? clonedData : parsed,
      dataJson: dataJson
    };
    lastRawMessages.unshift(diagnostic);
    if (lastRawMessages.length > MAX_RAW_MESSAGES) {
      lastRawMessages.length = MAX_RAW_MESSAGES;
    }

    if (objectKeys(fieldHits).length) {
      requestFieldHitsAcrossMessages.unshift({
        receivedAt: diagnostic.receivedAt,
        origin: origin,
        classification: classification,
        fields: fieldHits
      });
      if (requestFieldHitsAcrossMessages.length > MAX_RAW_MESSAGES) {
        requestFieldHitsAcrossMessages.length = MAX_RAW_MESSAGES;
      }
    }

    log('MessageEvent from trusted Cowlendar origin', {
      origin: origin,
      classification: classification,
      dataKeys: diagnostic.dataKeys,
      lineItemSummary: lineSummary
    });

    if (!parsed || typeof parsed !== 'object') return;

    // Keep any request-like payload even when it arrives before the cart message.
    var earlyRequest = extractBookRequestFromMessagePayload(parsed);
    if (earlyRequest && earlyRequest.form_data) {
      var earlyServiceId =
        parsed.service_id ||
        parsed.serviceId ||
        parsed.calendar_id ||
        parsed.calendarId ||
        'pending';
      var existing = lastBookByServiceId[earlyServiceId];
      if (existing && existing.bookResponse) {
        storeBookCapture(earlyServiceId, earlyRequest, existing.bookResponse, {
          source: 'postMessage-request-then-response',
          rawMessage: parsed
        });
      } else {
        lastBookByServiceId[earlyServiceId] = {
          serviceId: earlyServiceId === 'pending' ? null : earlyServiceId,
          bookRequest: deepClone(earlyRequest),
          bookResponse: existing ? existing.bookResponse : null,
          source: 'postMessage-request-only',
          rawMessage: safeDeepClone(parsed),
          capturedAt: Date.now()
        };
      }
    }

    if (!cartNode || !lineItemsHaveCowSignals(cartNode.line_items)) {
      return;
    }

    // Preserve the FULL trusted parent message unchanged for Worker/KV.
    var bookResponse = deepClone(parsed);

    var bookRequest = extractBookRequestFromMessagePayload(parsed);
    if (!bookRequest && cartNode !== parsed) {
      bookRequest = extractBookRequestFromMessagePayload(cartNode);
    }
    if (!bookRequest && lastBookByServiceId.pending && lastBookByServiceId.pending.bookRequest) {
      bookRequest = deepClone(lastBookByServiceId.pending.bookRequest);
    }

    var serviceId =
      parsed.service_id ||
      parsed.serviceId ||
      parsed.calendar_id ||
      parsed.calendarId ||
      null;

    storeBookCapture(serviceId, bookRequest, bookResponse, {
      source: 'postMessage',
      rawMessage: parsed
    });
  }

  function collectMatchingWindowKeys(root, pattern, limit) {
    var out = [];
    if (!root || typeof root !== 'object') return out;
    try {
      objectKeys(root).forEach(function (key) {
        if (out.length >= limit) return;
        if (pattern.test(key)) {
          var value;
          try {
            value = root[key];
          } catch (e) {
            value = '[access-error]';
          }
          out.push({
            key: key,
            type: typeof value,
            preview: serializeForDiagnostics(
              value && typeof value === 'object'
                ? objectKeys(value).slice(0, 40)
                : value
            ).slice(0, 500)
          });
        }
      });
    } catch (e) {
      /* ignore */
    }
    return out;
  }

  function collectStorageHits(storage, pattern) {
    var out = [];
    if (!storage) return out;
    try {
      for (var i = 0; i < storage.length; i += 1) {
        var key = storage.key(i);
        if (!key || !pattern.test(key)) continue;
        out.push({
          key: key,
          value: String(storage.getItem(key) || '').slice(0, 2000)
        });
      }
    } catch (e) {
      /* ignore */
    }
    return out;
  }

  /**
   * Parent-only probe. Does NOT read iframe.contentDocument / contentWindow docs.
   */
  function probeParentBookingState() {
    var pattern = /cow|cowlendar|bookeasy|book_easy|booking/i;
    var iframes = [];
    try {
      var nodes = document.querySelectorAll('iframe[src*="cowlendar"], iframe[src*="bookeasy"]');
      for (var i = 0; i < nodes.length; i += 1) {
        var src = nodes[i].getAttribute('src') || '';
        var abs = '';
        try {
          abs = new URL(src, window.location.href).href;
        } catch (e) {
          abs = src;
        }
        var params = {};
        try {
          var u = new URL(abs);
          u.searchParams.forEach(function (value, key) {
            params[key] = value;
          });
        } catch (e2) {
          /* ignore */
        }
        iframes.push({
          src: abs,
          queryParams: params
          // Intentionally omit contentWindow/document access.
        });
      }
    } catch (e) {
      /* ignore */
    }

    var hiddenInputs = [];
    try {
      var inputs = document.querySelectorAll(
        'input[type="hidden"][name*="cow" i], input[type="hidden"][id*="cow" i], input[type="hidden"][name*="book" i]'
      );
      for (var j = 0; j < inputs.length && j < 40; j += 1) {
        hiddenInputs.push({
          name: inputs[j].getAttribute('name'),
          id: inputs[j].getAttribute('id'),
          value: String(inputs[j].value || '').slice(0, 500)
        });
      }
    } catch (e) {
      /* ignore — :name*="cow" i may fail on older engines */
      try {
        var allHidden = document.querySelectorAll('input[type="hidden"]');
        for (var k = 0; k < allHidden.length && hiddenInputs.length < 40; k += 1) {
          var name = allHidden[k].getAttribute('name') || '';
          var id = allHidden[k].getAttribute('id') || '';
          if (pattern.test(name) || pattern.test(id)) {
            hiddenInputs.push({
              name: name,
              id: id,
              value: String(allHidden[k].value || '').slice(0, 500)
            });
          }
        }
      } catch (e2) {
        /* ignore */
      }
    }

    var dataAttrs = [];
    try {
      var attrNodes = document.querySelectorAll(
        '[data-cow], [data-cowlendar], [data-bookeasy], [class*="cowlendar"], [id*="cowlendar"], [class*="bookeasy"], [id*="bookeasy"]'
      );
      for (var n = 0; n < attrNodes.length && dataAttrs.length < 40; n += 1) {
        var el = attrNodes[n];
        var attrs = {};
        for (var a = 0; a < el.attributes.length; a += 1) {
          var attr = el.attributes[a];
          if (/^data-/i.test(attr.name) || pattern.test(attr.name) || pattern.test(attr.value)) {
            attrs[attr.name] = String(attr.value || '').slice(0, 300);
          }
        }
        dataAttrs.push({
          tag: el.tagName,
          id: el.id || null,
          className: String(el.className || '').slice(0, 200),
          attrs: attrs
        });
      }
    } catch (e) {
      /* ignore */
    }

    var report = {
      probedAt: new Date().toISOString(),
      href: window.location.href,
      windowKeys: collectMatchingWindowKeys(window, pattern, 60),
      localStorage: collectStorageHits(window.localStorage, pattern),
      sessionStorage: collectStorageHits(window.sessionStorage, pattern),
      cowlendarIframes: iframes,
      hiddenInputs: hiddenInputs,
      relatedDom: dataAttrs,
      trustedMessageCount: lastRawMessages.length,
      requestFieldHitsAcrossMessages: requestFieldHitsAcrossMessages.slice(),
      note:
        'Parent-only probe. Same-origin iframe document access is intentionally not attempted.'
    };

    log('Parent booking-state probe', report);
    log('Parent booking-state probe FULL JSON', serializeForDiagnostics(report));
    return report;
  }

  function dumpMessages() {
    var payload = {
      dumpedAt: new Date().toISOString(),
      count: lastRawMessages.length,
      messages: lastRawMessages.slice(),
      requestFieldHitsAcrossMessages: requestFieldHitsAcrossMessages.slice()
    };
    log('dumpMessages count=' + payload.count);
    log('dumpMessages FULL JSON', serializeForDiagnostics(payload));
    return payload;
  }

  window.addEventListener(
    'message',
    function (event) {
      if (!event || !event.origin) return;
      if (!TRUSTED_MESSAGE_ORIGINS[event.origin]) return;
      try {
        ingestParentMessage(event.origin, event.data);
      } catch (err) {
        errorLog('Failed processing Cowlendar MessageEvent', err);
      }
    },
    true
  );

  /* ---------------------------- fetch patch ---------------------------- */

  function getFetchUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return String(input);
  }

  function getFetchMethod(input, init) {
    if (init && init.method) return String(init.method).toUpperCase();
    if (input && input.method) return String(input.method).toUpperCase();
    return 'GET';
  }

  function getFetchBodySource(input, init) {
    if (init && init.body != null) return Promise.resolve(init.body);
    if (input && typeof Request !== 'undefined' && input instanceof Request) {
      try {
        return input.clone().text();
      } catch (e) {
        return Promise.resolve('');
      }
    }
    return Promise.resolve(null);
  }

  if (typeof window.fetch === 'function') {
    var originalFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var url = getFetchUrl(input);
      var method = getFetchMethod(input, init);

      // Keep parent-network /book capture if a future integration moves it out of the iframe.
      if (method === 'POST' && isCowlendarBookUrl(url)) {
        return getFetchBodySource(input, init).then(function (bodySource) {
          return readBodyAsText(bodySource).then(function (requestText) {
            return originalFetch(input, init).then(function (response) {
              response
                .clone()
                .text()
                .then(function (responseText) {
                  captureBookFromNetwork(url, requestText, responseText);
                })
                .catch(function () {});
              return response;
            });
          });
        });
      }

      if (method === 'POST' && isCartAddUrl(url)) {
        return getFetchBodySource(input, init).then(function (bodySource) {
          return readBodyAsText(bodySource).then(function (bodyText) {
            var interceptPromise = maybeInterceptCartAdd(bodyText);
            if (interceptPromise) return interceptPromise;
            return originalFetch(input, init);
          });
        });
      }

      return originalFetch(input, init);
    };
  }

  /* ----------------------------- XHR patch ----------------------------- */

  if (typeof XMLHttpRequest !== 'undefined') {
    var xhrOpen = XMLHttpRequest.prototype.open;
    var xhrSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__tcwMethod = String(method || 'GET').toUpperCase();
      this.__tcwUrl = url;
      return xhrOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function (body) {
      var xhr = this;
      var method = xhr.__tcwMethod || 'GET';
      var url = xhr.__tcwUrl || '';

      if (method === 'POST' && isCowlendarBookUrl(url)) {
        readBodyAsText(body).then(function (requestText) {
          xhr.addEventListener('load', function () {
            captureBookFromNetwork(url, requestText, xhr.responseText || '');
          });
        });
        return xhrSend.call(xhr, body);
      }

      if (method === 'POST' && isCartAddUrl(url)) {
        return readBodyAsText(body).then(function (bodyText) {
          var interceptPromise = maybeInterceptCartAdd(bodyText);
          if (!interceptPromise) {
            return xhrSend.call(xhr, body);
          }

          interceptPromise.then(
            function () {
              // Hosted redirect, or quiet user cancel — do not surface XHR errors.
            },
            function () {
              try {
                Object.defineProperty(xhr, 'status', { configurable: true, value: 0 });
                Object.defineProperty(xhr, 'readyState', { configurable: true, value: 4 });
                if (typeof xhr.onerror === 'function') xhr.onerror(new Event('error'));
                xhr.dispatchEvent(new Event('error'));
              } catch (e) {
                /* ignore */
              }
            }
          );
        });
      }

      return xhrSend.call(xhr, body);
    };
  }

  // Capture-phase custom-event observer (parent only). Observe/copy; do not stopPropagation.
  var CUSTOM_EVENT_LOG = [];
  document.addEventListener(
    'DOMContentLoaded',
    function () {
      /* no-op marker for load order */
    },
    true
  );
  [
    'cow',
    'cowlendar',
    'bookeasy',
    'booking',
    'CowCart',
    'cow-cart',
    'cow_cart_data'
  ].forEach(function (name) {
    try {
      window.addEventListener(
        name,
        function (event) {
          var entry = {
            receivedAt: new Date().toISOString(),
            type: event.type,
            detail: safeDeepClone(event.detail),
            detailJson: serializeForDiagnostics(event.detail)
          };
          CUSTOM_EVENT_LOG.unshift(entry);
          if (CUSTOM_EVENT_LOG.length > 20) CUSTOM_EVENT_LOG.length = 20;
          log('CustomEvent observed on window', entry);
        },
        true
      );
    } catch (e) {
      /* ignore */
    }
  });

  window.__TCW_SERVICE_PAYMENTS__ = {
    TRUSTED_MESSAGE_ORIGINS: TRUSTED_MESSAGE_ORIGINS,
    DEBUG: DEBUG,
    LIABILITY_WAIVER_PATH: LIABILITY_WAIVER_PATH,
    getLiabilityWaiverUrl: getLiabilityWaiverUrl,
    extractLiabilityWaiverContent: extractLiabilityWaiverContent,
    parseCartAddLines: parseCartAddLines,
    isCowlendarServiceLine: isCowlendarServiceLine,
    canContinueToPayment: canContinueToPayment,
    findCartDataNode: findCartDataNode,
    extractBookRequestFromMessagePayload: extractBookRequestFromMessagePayload,
    scanRequestFieldHits: scanRequestFieldHits,
    classifyTrustedMessage: classifyTrustedMessage,
    summarizeLineItems: summarizeLineItems,
    findLineItemsArray: findLineItemsArray,
    validateRequiredBookingCapture: validateRequiredBookingCapture,
    getCaptures: function () {
      return {
        byServiceId: lastBookByServiceId,
        byVariantId: lastBookByVariantId
      };
    },
    getLastMessages: function () {
      return lastRawMessages.slice();
    },
    dumpMessages: dumpMessages,
    probeParentBookingState: probeParentBookingState,
    getCustomEvents: function () {
      return CUSTOM_EVENT_LOG.slice();
    },
    getRequestFieldHits: function () {
      return requestFieldHitsAcrossMessages.slice();
    }
  };

  if (DEBUG) {
    log('Intercept ready (dynamic Cowlendar eligibility; debug logging on)');
  }
})();
